import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { Check, ChevronDown, Info, ListChecks, X } from 'lucide-react'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { cardFeatures, effectivePriceCents, formatPrice, isUpgrade, limitLines, tiersToCompare } from '../../data/catalog'
import { CollapsibleCard, DarkButton, Eyebrow, FeatureItem, OutlineButton, Pill } from './BillingUi'
import { PLAN_CARD_CLASS, PlanGrid } from './PlanGrid'
import { PriceBlock } from './PriceBlock'

// The recommended plan is the visually emphasized card (PRD §6.3); every other plan shares one style.
const RECOMMENDED_ACCENT = { bg: 'bg-gradient-to-b from-[#EFF4FF] to-white', border: 'border-2 border-[#2563EB]', price: 'text-[#2563EB]' }
const STANDARD_ACCENT = { bg: 'bg-gradient-to-b from-sky-50 to-white', border: 'border border-sky-200', price: 'text-sky-700' }
// The customer's own plan gets a green outline, matching its "Current plan" badge.
const CURRENT_BORDER = 'border-2 border-[#16A34A]'

// Badges that sit on a card's top border; a plan that is both current and recommended shows them side by side.
const BADGE_CLASS = 'rounded-md px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white'

// Feature lines a collapsed card shows; the rest, the limits and the perks wait behind "Show all features".
const KEY_FEATURE_COUNT = 2

// Every card button is the same height, whichever style it takes.
const CARD_BUTTON_CLASS = '!h-10 w-full whitespace-nowrap !px-3 !text-[13px]'

/**
 * Decide what the card's button does for this customer (month-to-month, self-serve):
 *  - no Stripe subscription (new, canceled, in-app trial) → Checkout for that plan
 *  - Stripe subscription, bigger plan → prorated upgrade, in-app
 *  - Stripe subscription, smaller plan → a support ticket; our team makes downgrades
 */
const resolveAction = ({ catalog, tier, currentTierKey, hasStripeSubscription, downgradeInProgress }) => {
  if (!hasStripeSubscription) {
    return { kind: 'checkout', label: currentTierKey ? 'Switch to this plan' : 'Subscribe' }
  }
  if (isUpgrade(catalog, currentTierKey, tier.id)) {
    return { kind: 'upgrade', label: `Upgrade to ${tier.name}` }
  }
  if (downgradeInProgress) {
    return {
      kind: 'downgrade',
      label: 'Downgrade in progress',
      disabled: true,
      note: 'Waiting for our team — you can cancel it on Billing.'
    }
  }
  return {
    kind: 'downgrade',
    label: 'Request downgrade',
    note: 'Our team makes the change at the end of your period; you can cancel the request on Billing.'
  }
}

/** One plan card; every card renders the same slots so they line up across the row (see PLAN_CARD_CLASS). */
const PlanCard = ({ tier, currentTierKey, features, resolve, pending, expanded, onToggle, onCheckout, onUpgrade, onDowngrade }) => {
  const isCurrent = tier.id === currentTierKey
  const action = resolve(tier)
  const accent = tier.recommended ? RECOMMENDED_ACCENT : STANDARD_ACCENT
  const handlers = {
    checkout: () => onCheckout?.(tier.id),
    upgrade: () => onUpgrade?.(tier.id),
    downgrade: () => onDowngrade?.(tier.id)
  }
  const Button = tier.recommended ? DarkButton : OutlineButton
  const limits = limitLines(tier)
  const shownFeatures = expanded ? features.items : features.items.slice(0, KEY_FEATURE_COUNT)
  const hiddenCount = features.items.length - KEY_FEATURE_COUNT + limits.length + tier.perks.length
  return (
    <AdasBox
      className={`relative rounded-2xl p-5 ${PLAN_CARD_CLASS} ${accent.bg} ${isCurrent ? CURRENT_BORDER : accent.border} ${
        tier.recommended || isCurrent ? 'shadow-lg' : 'shadow-md'
      }`}
    >
      {(isCurrent || tier.recommended) && (
        <span className='absolute -top-3 left-5 flex gap-1.5'>
          {isCurrent && <span className={`${BADGE_CLASS} bg-[#16A34A]`}>Current plan</span>}
          {tier.recommended && <span className={`${BADGE_CLASS} bg-[#0A1628]`}>Recommended</span>}
        </span>
      )}
      {/* Every slot below is one shared row (see PLAN_CARD_CLASS): always rendered, even when empty. */}
      <AdasTypography className='text-[18px] font-bold text-foreground'>{tier.name}</AdasTypography>
      <AdasTypography className='mt-1 text-[13px] text-muted-foreground'>{tier.tagline}</AdasTypography>

      <AdasBox className='mt-3'>
        <PriceBlock tier={tier} priceClass={accent.price} />
      </AdasBox>

      <AdasBox className='mt-4 flex flex-col gap-0.5'>
        {/* The toggle sits at the foot of this shared row, so it lines up across the cards. */}
        {features.heading && (
          <AdasTypography className='mb-1 border-b border-border/70 pb-2 text-[13px] font-semibold text-muted-foreground'>
            {features.heading}
          </AdasTypography>
        )}
        {shownFeatures.map((label) => (
          <FeatureItem key={label} included compact label={label} />
        ))}
        {hiddenCount > 0 && (
          <button
            type='button'
            onClick={onToggle}
            aria-expanded={expanded}
            className='mt-auto inline-flex items-center gap-1 self-center pt-2 text-[13px] font-semibold text-[#2563EB] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/40'
          >
            {expanded ? 'Show less' : 'Show all features'}
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </AdasBox>

      <AdasBox
        className={expanded && limits.length > 0 ? 'mt-3 flex flex-col gap-0.5 border-t border-border/70 pt-2' : ''}
        aria-label='Plan limits'
      >
        {expanded &&
          limits.map((label) => (
            <AdasTypography key={label} className='text-[12px] text-foreground'>
              · {label}
            </AdasTypography>
          ))}
      </AdasBox>

      <AdasBox className={expanded && tier.perks.length > 0 ? 'mt-3 border-t border-border/70 pt-3' : ''}>
        {expanded &&
          tier.perks.map((label) => (
            <AdasTypography key={label} className='text-[12px] text-muted-foreground'>
              · {label}
            </AdasTypography>
          ))}
      </AdasBox>

      <AdasBox className='mt-1 w-full self-end'>
        {isCurrent ? (
          <OutlineButton fullWidth className={`${CARD_BUTTON_CLASS} cursor-default opacity-70`}>
            Your current plan
          </OutlineButton>
        ) : (
          <Button fullWidth className={CARD_BUTTON_CLASS} onClick={handlers[action.kind]} disabled={pending || action.disabled}>
            {pending && !action.disabled ? (
              'Opening…'
            ) : (
              <>
                <span className='truncate'>{action.label}</span>
                {/* Points at the note in the banner under the cards. */}
                {action.note && <span aria-hidden='true'>*</span>}
              </>
            )}
          </Button>
        )}
      </AdasBox>
    </AdasBox>
  )
}
PlanCard.propTypes = {
  tier: PropTypes.object.isRequired,
  currentTierKey: PropTypes.string,
  /** The features section: `{ heading, items }` (cardFeatures). */
  features: PropTypes.shape({ heading: PropTypes.string, items: PropTypes.arrayOf(PropTypes.string) }).isRequired,
  /** `(tier) → { kind, label, note?, disabled? }` for the card's plan. */
  resolve: PropTypes.func.isRequired,
  pending: PropTypes.bool,
  /** Whether the full feature list, limits and perks are showing (shared by every card so rows stay aligned). */
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onCheckout: PropTypes.func,
  onUpgrade: PropTypes.func,
  onDowngrade: PropTypes.func
}

const MatrixCell = ({ included }) =>
  included ? (
    <Check className='mx-auto h-4 w-4 text-[#16A34A]' strokeWidth={2.5} aria-label='Included' />
  ) : (
    <X className='mx-auto h-4 w-4 text-[#CBD5E1]' strokeWidth={2.5} aria-label='Not included' />
  )
MatrixCell.propTypes = { included: PropTypes.bool }

/** Cheapest plan first; a plan without a price goes last, ties fall back to the catalog rank. */
const byPrice = (tiers) =>
  [...tiers].sort((a, b) => (effectivePriceCents(a) ?? Infinity) - (effectivePriceCents(b) ?? Infinity) || a.rank - b.rank)

/**
 * Plan comparison (PRD §6.3): one card per plan on sale (plus the customer's own plan if it has
 * been retired), cheapest first and side by side, the current plan marked, and the full feature
 * matrix. Everything comes from the
 * plan catalog (`catalog`, from GET /v1/public/billing/catalog) — no plan is known by name here.
 * Each card's button resolves to Checkout, an upgrade, or a downgrade request (see resolveAction).
 */
export const PlanComparison = ({
  catalog,
  currentTierKey,
  hasStripeSubscription = false,
  pending = false,
  downgradeInProgress = false,
  onCheckout,
  onUpgrade,
  onDowngrade
}) => {
  const tiers = byPrice(tiersToCompare(catalog, currentTierKey))
  const modules = catalog?.modules ?? []
  // One toggle for every card: the cards share row tracks, so expanding just one would stretch them all anyway.
  const [expanded, setExpanded] = useState(false)
  const resolve = (tier) => resolveAction({ catalog, tier, currentTierKey, hasStripeSubscription, downgradeInProgress })
  // Notes for the buttons marked with an asterisk, said once below the cards rather than inside each one.
  const notes = [...new Set(tiers.filter((tier) => tier.id !== currentTierKey).map((tier) => resolve(tier).note))].filter(Boolean)
  const renderCard = (tier) => (
    <PlanCard
      key={tier.id}
      tier={tier}
      currentTierKey={currentTierKey}
      features={cardFeatures(tier)}
      resolve={resolve}
      pending={pending}
      expanded={expanded}
      onToggle={() => setExpanded((open) => !open)}
      onCheckout={onCheckout}
      onUpgrade={onUpgrade}
      onDowngrade={onDowngrade}
    />
  )
  return (
    <AdasBox>
      <Eyebrow>Plans</Eyebrow>
      <AdasTypography className='mt-1 text-[22px] font-bold text-foreground'>Compare plans</AdasTypography>
      <AdasTypography className='mt-1 text-[14px] text-muted-foreground'>
        Payments are processed securely by Stripe. Plans are month-to-month: upgrades apply immediately and are prorated, and you can cancel
        any time in the billing portal. To move to a smaller plan, request a downgrade and our team will make the change.
      </AdasTypography>

      <PlanGrid count={tiers.length} className='mt-6'>
        {tiers.map(renderCard)}
      </PlanGrid>

      {notes.length > 0 && (
        <AdasBox className='mt-4 flex items-start gap-2.5 rounded-xl border border-[#D1D5D8] bg-[#F8FAFC] px-4 py-3'>
          <Info className='mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]' />
          <AdasBox>
            {notes.map((note) => (
              <AdasTypography key={note} className='text-[13px] text-foreground'>
                * {note}
              </AdasTypography>
            ))}
          </AdasBox>
        </AdasBox>
      )}

      <AdasBox className='mt-8'>
        <CollapsibleCard
          title='All features'
          icon={ListChecks}
          action={<Pill label={`${modules.length} features`} tone='slate' dot={false} />}
        >
          <AdasBox className='w-full overflow-auto'>
            <table className='w-full border-collapse text-left'>
              <thead>
                <tr className='border-b border-[#E5E7EB] bg-[#F8FAFC]'>
                  <th scope='col' className='h-11 px-4 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground'>
                    Module
                  </th>
                  {tiers.map((tier) => (
                    <th
                      key={tier.id}
                      scope='col'
                      className='h-11 px-4 text-center text-[12px] font-semibold uppercase tracking-wide text-muted-foreground'
                    >
                      {tier.name}
                      <span className='block text-[11px] font-normal normal-case text-muted-foreground/80'>
                        {formatPrice(tier.priceCents)}/mo
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modules.map((module) => (
                  <tr key={module.id} className='border-b border-[#EBEDF0] even:bg-[#F8FAFC] hover:bg-[#F0F4FF]'>
                    <td className='px-4 py-2.5 align-middle'>
                      <span className='block text-[13px] text-[#0A1628]'>{module.name}</span>
                    </td>
                    {tiers.map((tier) => (
                      <td key={tier.id} className='px-4 py-2.5 text-center align-middle'>
                        <MatrixCell included={tier.modules.includes(module.id)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </AdasBox>
        </CollapsibleCard>
      </AdasBox>
    </AdasBox>
  )
}
PlanComparison.propTypes = {
  catalog: PropTypes.shape({ tiers: PropTypes.array, modules: PropTypes.array }),
  currentTierKey: PropTypes.string,
  hasStripeSubscription: PropTypes.bool,
  pending: PropTypes.bool,
  onCheckout: PropTypes.func,
  onUpgrade: PropTypes.func,
  downgradeInProgress: PropTypes.bool,
  onDowngrade: PropTypes.func
}
